import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { getPaymentTracking } from '../../../services/loanService';
import type { PaymentTrackingRecord, PaymentTrackingPage, GetPaymentTrackingStatus } from '../../../services/schemas/loanServiceSchemas';
import './modal.css';

const PaymentOverview = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const ALL_PAYMENTS_PAGE_SIZE = 10000;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState<GetPaymentTrackingStatus | ''>('');
  const [customerName, setCustomerName] = useState('');
  const navigate = useNavigate();

  // State for payment tracking data
  const [paymentTracking, setPaymentTracking] = useState<PaymentTrackingPage>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    page: 0
  });

  // Fetch payment tracking data from backend API
  const fetchPaymentTracking = async (month: Date) => {
    setIsLoading(true);
    try {
      // Format the month as YYYY-MM-DD (first day of the month)
      const targetMonth = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-01`;
      
      // Prepare API parameters
      const params = {
        targetMonth,
        page: 0,
        size: ALL_PAYMENTS_PAGE_SIZE,
        ...(filterStatus && { status: filterStatus as GetPaymentTrackingStatus }),
        ...(customerName.trim() && { customerName: customerName.trim() })
      };

      const response = await getPaymentTracking(params);
      
      // Update state with API response
      setPaymentTracking(response || {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 10,
        page: 0
      });

    } catch (error) {
      console.error('Error fetching payment tracking:', error);
      toast.error('Failed to fetch payment tracking data');
      
      // Set empty state on error
      setPaymentTracking({
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 10,
        page: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when filters or pagination changes
  useEffect(() => {
    fetchPaymentTracking(selectedMonth);
  }, [filterStatus, customerName, selectedMonth]);

  // Handle month change
  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + '-01');
    setSelectedMonth(newDate);
  };

  // Format selected month for display
  const formatSelectedMonth = (date: Date) => {
    return date.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Current':
        return 'success';
      case 'Overdue':
        return 'danger';
      case 'Paid':
        return 'primary';
      case 'Partial':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setSize(newPerPage);
    setPage(1); // Reset to first page
  };

  // Handle filter change
  const handleFilterChange = (status: string) => {
    setFilterStatus(status as GetPaymentTrackingStatus | '');
    setPage(1); // Reset to first page when filter changes
  };

  // Handle customer name search
  const handleCustomerNameSearch = (name: string) => {
    setCustomerName(name);
    setPage(1); // Reset to first page when search changes
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterStatus('');
    setCustomerName('');
    setPage(1);
  };

  // Define table columns
  const columns = [
    {
      name: 'Customer',
      selector: (row: PaymentTrackingRecord) => row.customerName || '',
      sortable: true,
      grow: 2,
    },
    {
      name: 'Expected',
      selector: (row: PaymentTrackingRecord) => row.expectedAmount || 0,
      sortable: true,
      format: (row: PaymentTrackingRecord) => formatCurrency(row.expectedAmount || 0),
    },
    {
      name: 'Interest',
      selector: (row: PaymentTrackingRecord) => row.interestEarned || 0,
      sortable: true,
      format: (row: PaymentTrackingRecord) => formatCurrency(row.interestEarned || 0),
    },
    {
      name: 'Collected',
      selector: (row: PaymentTrackingRecord) => row.amountCollected || 0,
      sortable: true,
      format: (row: PaymentTrackingRecord) => formatCurrency(row.amountCollected || 0),
    },
    {
      name: 'Remaining',
      selector: (row: PaymentTrackingRecord) => row.remainingAmount || 0,
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <span className={(row.remainingAmount || 0) > 0 ? 'text-danger fw-bold' : 'text-success'}>
          {formatCurrency(row.remainingAmount || 0)}
        </span>
      ),
    },
    {
      name: 'Due Date',
      selector: (row: PaymentTrackingRecord) => row.expectedPaymentDueDate || '',
      sortable: true,
      format: (row: PaymentTrackingRecord) => formatDate(row.expectedPaymentDueDate || ''),
    },
    {
      name: 'Last Pay',
      selector: (row: PaymentTrackingRecord) => row.lastPayDate || '',
      sortable: true,
      format: (row: PaymentTrackingRecord) => formatDate(row.lastPayDate || ''),
    },
    {
      name: 'Status',
      selector: (row: PaymentTrackingRecord) => row.status || 'Current',
      sortable: true,
      cell: (row: PaymentTrackingRecord) => (
        <span className={`badge bg-${getStatusColor(row.status || 'Current')}`}>
          {row.status || 'Current'}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: (row: PaymentTrackingRecord) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => navigate(`/loan-accounts/${row.loanAccountId || ''}`)}
            data-tooltip-id={`view-${row.id || ''}`}
          >
            <i className="fa fa-eye"></i>
          </button>
          <Tooltip id={`view-${row.id || ''}`} place="top" content="View details" />
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/">
                    <i className="fa fa-home me-2"></i>Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/collections">Collections</Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">Loan Book</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="fs-2 fw-bold mb-2 text-white">
                Loan Book 📋
              </h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                Loan records for {formatSelectedMonth(selectedMonth)}
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="d-flex gap-3 justify-content-md-end align-items-center">
                <div className="d-flex align-items-center bg-white text-dark rounded-pill px-4 py-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-calendar me-2 text-primary"></i>
                  <input
                    type="month"
                    className="border-0 bg-transparent fw-medium"
                    style={{ outline: 'none', minWidth: '120px' }}
                    value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
                    onChange={handleMonthChange}
                  />
                </div>
                <div className="d-inline-flex align-items-center bg-white text-dark rounded-pill px-4 py-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-clock-o me-2 text-success"></i>
                  <span className="fw-medium">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">

          {/* Enhanced Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <div className="card-header bg-transparent border-0 pb-0">
                  <div className="d-flex align-items-center">
                    <div className="bg-light-primary rounded-3 p-3 me-3">
                      <i className="fa fa-search text-primary fs-4"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Search & Filter</h5>
                      <p className="text-muted mb-0 small">Find specific loan records</p>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-3">
                  <div className="row g-3 align-items-end">
                    {/* Search Bar - Full Width on Mobile, 6 columns on larger screens */}
                    <div className="col-12 col-lg-6">
                      <label className="form-label fw-semibold text-dark">
                        <i className="fa fa-user me-2 text-muted"></i>Customer Search
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0">
                          <i className="fa fa-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          placeholder="Search by customer name or loan reference..."
                          value={customerName}
                          onChange={(e) => handleCustomerNameSearch(e.target.value)}
                          style={{ boxShadow: 'none' }}
                        />
                        {customerName && (
                          <button 
                            className="btn btn-outline-secondary border-start-0"
                            type="button"
                            onClick={() => handleCustomerNameSearch('')}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Status Filter */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="form-label fw-semibold text-dark">
                        <i className="fa fa-filter me-2 text-muted"></i>Status Filter
                      </label>
                      <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        style={{ boxShadow: 'none' }}
                      >
                        <option value="">All Status</option>
                        <option value="Current">✅ Current</option>
                        <option value="Overdue">🔴 Overdue</option>
                        <option value="Paid">💚 Paid</option>
                        <option value="Partial">🟡 Partial</option>
                      </select>
                    </div>

                    {/* Action Buttons - Far Right */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="d-flex gap-2 justify-content-lg-end">
                        <button 
                          className="btn btn-outline-secondary d-flex align-items-center"
                          onClick={clearFilters}
                          style={{ minWidth: '100px' }}
                        >
                          <i className="fa fa-refresh me-2"></i>Clear
                        </button>
                        <button 
                          className="btn btn-primary d-flex align-items-center"
                          onClick={() => fetchPaymentTracking(selectedMonth)}
                          style={{ minWidth: '100px' }}
                        >
                          <i className="fa fa-search me-2"></i>Search
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Summary */}
                  {(filterStatus || customerName) && (
                    <div className="mt-3 p-3 bg-light rounded-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <small className="text-muted fw-medium">Active Filters:</small>
                          {filterStatus && (
                            <span className="badge bg-primary-subtle text-primary">
                              Status: {filterStatus}
                            </span>
                          )}
                          {customerName && (
                            <span className="badge bg-info-subtle text-info">
                              Search: "{customerName}"
                            </span>
                          )}
                        </div>
                        <button 
                          className="btn btn-sm btn-link text-decoration-none p-0"
                          onClick={clearFilters}
                        >
                          <i className="fa fa-times me-1"></i>Clear All
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Records Table */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="card-title mb-0">Loan Records</h6>
                  <div className="d-flex align-items-center gap-3">
                    <small className="text-muted">
                      <strong>{paymentTracking.content?.length || 0}</strong> of <strong>{paymentTracking.content?.length || 0}</strong> records
                    </small>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate('/collections/payment-summary')}
                    >
                      <i className="fa fa-bar-chart me-1"></i>Loan Book Summary
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                                                <p className="mt-2">Loading loan records...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={columns}
                        data={paymentTracking.content || []}
                        pagination
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationPerPage={size}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        striped
                        responsive
                        progressPending={isLoading}
                        noDataComponent={
                          <div className="text-center py-4">
                            <i className="fa fa-search fs-1 text-muted mb-3"></i>
                            <h5 className="text-muted">No payment records found</h5>
                            <p className="text-muted">Try adjusting your search filters</p>
                          </div>
                        }
                        paginationComponentOptions={{
                          rowsPerPageText: 'Rows per page:',
                          rangeSeparatorText: 'of',
                          selectAllRowsItem: true,
                          selectAllRowsItemText: 'All',
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentOverview; 
